'use client'

import { useCallback } from 'react'
import { fromInteger } from '@repo/money'
import {
  useGetCurrentPlayerToken,
  useIsAuthenticated,
  useIsDemoMode,
  usePlayerActions
} from 'src/store/player'
import { useDemoStore } from 'src/store/demo'
import type { Balance } from 'src/types/domain/Player'
import { promisifyPush } from 'src/utils/promisifyPush'
import { useGetPlayerChannel } from 'src/store/socket'

interface GetPlayerBalanceOutput {
  data: Balance
}

export function usePlayerBalance() {
  const token = useGetCurrentPlayerToken()
  const isAuthenticated = useIsAuthenticated()
  const isDemoMode = useIsDemoMode()
  const playerActions = usePlayerActions()
  const playerChannel = useGetPlayerChannel()

  const getPlayerBalance = useCallback(async (): Promise<GetPlayerBalanceOutput | undefined> => {
    if (!playerChannel || playerChannel.state !== 'joined') {
      console.warn(
        'Cannot get balance. Player channel not ready, state:',
        playerChannel?.state || 'undefined'
      )
      // Don't capture exception for temporary unavailability - this is expected during reconnections
      return
    }

    try {
      const result = await promisifyPush({
        channel: playerChannel,
        eventName: 'client:player:balance:get'
      })

      const responseDTO = result as Balance

      return {
        data: responseDTO
      }
    } catch (error) {
      console.error('Getting balance failed:', error)
    }
  }, [playerChannel])

  const updateBalance = useCallback(async () => {
    if (!isAuthenticated) return

    if (isDemoMode) {
      // Read the demo store's CURRENT balance at call time. Event handlers
      // call updateBalance() synchronously in the same tick as the optimistic
      // credit that precedes it — any render-captured value (prop, selector,
      // or ref written during render) still holds the PRE-credit balance
      // there, and re-applying it wipes the credit and, via balanceChanged's
      // unconditional mirror, poisons the demo store itself. getState() also
      // keeps this callback referentially stable (no store subscription), so
      // usePlayerListeners doesn't re-register its channel listeners on every
      // balance change.
      playerActions.balanceChanged(useDemoStore.getState().balance)
      return
    }

    if (!token) {
      console.warn('updateBalance called without token')
      return
    }

    const result = await getPlayerBalance()

    if (typeof result !== 'object') {
      console.error('Getting balance failed: response undefined')
      return
    }

    if ('error' in result) {
      console.error('Getting balance failed:', result.error)
      return
    }

    const balance: Balance = {
      amount: fromInteger(result.data.amount, result.data.currency),
      currency: result.data.currency
    }

    playerActions.balanceChanged(balance)
  }, [isAuthenticated, getPlayerBalance, isDemoMode, token, playerActions])

  return { updateBalance }
}
